import { useEffect, useRef, useState } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { PLAN_PAYMENT_ERROR_KEY } from '../constants/planPayment.constants';
import { buildPlanOrderStatusReturnUrl } from '../helpers/buildPlanOrderStatusReturnUrl';

export const usePlanPaymentConfirm = ({
	intent,
	returnPath = '/studio',
	email,
	billingCountry,
	initialErrorMessage,
	onConfirmError,
}) => {
	const stripe = useStripe();
	const elements = useElements();
	const [errorMessage, setErrorMessage] = useState(initialErrorMessage || '');
	const [isProcessing, setIsProcessing] = useState(false);
	const isSubmittingRef = useRef(false);

	useEffect(() => {
		const storedError = sessionStorage.getItem(PLAN_PAYMENT_ERROR_KEY);
		if (!storedError) {
			return;
		}

		sessionStorage.removeItem(PLAN_PAYMENT_ERROR_KEY);
		setErrorMessage(storedError);
	}, []);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setErrorMessage('');

		if (
			!stripe ||
			!elements ||
			!intent?.clientSecret ||
			isSubmittingRef.current
		) {
			return;
		}

		isSubmittingRef.current = true;
		setIsProcessing(true);

		try {
			const { error: submitError } = await elements.submit();
			if (submitError) {
				setErrorMessage(submitError.message ?? 'Payment validation failed.');
				isSubmittingRef.current = false;
				setIsProcessing(false);
				return;
			}

			const confirmIntent =
				intent.type === 'setup' ? stripe.confirmSetup : stripe.confirmPayment;

			const { error } = await confirmIntent({
				elements,
				clientSecret: intent.clientSecret,
				confirmParams: {
					return_url: buildPlanOrderStatusReturnUrl({ returnPath, email }),
					payment_method_data: {
						billing_details: {
							...(email && { email }),
							address: {
								country: billingCountry,
							},
						},
					},
				},
			});

			if (error) {
				setErrorMessage(error.message ?? 'Payment failed.');
				isSubmittingRef.current = false;
				setIsProcessing(false);
				onConfirmError?.(error);
			}
		} catch (error) {
			setErrorMessage(error?.message ?? 'Payment failed.');
			isSubmittingRef.current = false;
			setIsProcessing(false);
		}
	};

	return {
		stripe,
		elements,
		isProcessing,
		errorMessage,
		handleSubmit,
	};
};
